import React from 'react';
import {
  Lock, FileCheck2, Percent, FileText, ListChecks, Database, UserCheck, ShieldCheck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import type { GovernanceData, AaiPolicy, WhatsAllowedData } from '../../../types/aai';
import { useAsync } from '../useAsync';
import { Loading, ErrorNote } from '../components';
import { Card, MonoLabel, StatCard, RiskPill, PageHeader } from '../dashboards/ui';

// The standing privacy guardrails — described qualitatively (these are how the
// platform behaves), not as hardcoded thresholds the page can't source.
const PRIVACY_CONTROLS = [
  { icon: Lock, name: 'Group-size suppression', detail: 'A department below the minimum group size is hidden — never shown — to protect individuals.' },
  { icon: Percent, name: 'Self-report discount', detail: 'Self-reported gains are discounted before they roll into any team or company number.' },
  { icon: FileCheck2, name: 'Audited individual access', detail: 'Opening one person’s readiness writes an audit-log entry (who, when, who was viewed) before the data returns.' },
];

// ── Local, design-matched helpers (kept in this file so the shared kit is untouched) ──

// Honest date formatter for the policy "updated" line — no fabricated dates.
const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
const distinct = (xs: string[]) => Array.from(new Set(xs.filter(Boolean)));

// Policy-card status pill: green Active (published / live), brand Enforced
// (platform-enforced behaviour), muted gray for an honest not-set state.
const POLICY_PILL: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700',
  enforced: 'bg-brand-50 text-brand-700',
};
const PolicyStatus: React.FC<{ label: string; muted?: boolean }> = ({ label, muted }) => (
  <span className={`text-[10.5px] font-bold rounded-full px-2.5 py-0.5 whitespace-nowrap ${
    muted ? 'bg-gray-100 text-gray-500' : POLICY_PILL[label.toLowerCase()] || 'bg-gray-100 text-gray-600'
  }`}>{label}</span>
);

// One policy card — icon tile + title + status pill + note (design lines 794-797).
const PolicyCard: React.FC<{
  icon: LucideIcon; title: string; note: string; status: string; muted?: boolean;
}> = ({ icon: Icon, title, note, status, muted }) => (
  <div className="bg-white border border-gray-100 rounded-xl p-[18px] flex gap-3">
    <span className="w-10 h-10 rounded-xl bg-brand-50 text-brand-500 grid place-items-center shrink-0">
      <Icon size={18} />
    </span>
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <span className="font-bold text-[13.5px]">{title}</span>
        <PolicyStatus label={status} muted={muted} />
      </div>
      <div className="text-[12px] text-gray-500 mt-1 leading-snug">{note}</div>
    </div>
  </div>
);

// Status colour for the examiner controls table — pass = emerald, open finding = red.
const controlTone = (status: string) => {
  const s = (status || '').toLowerCase();
  if (/(pass|clear|complete|resolved|\bok\b|green)/.test(s)) return 'text-emerald-600';
  if (/(finding|open|fail|block|gap|overdue|red)/.test(s)) return 'text-red-600';
  return 'text-amber-600';
};

const RiskGovernancePage: React.FC = () => {
  const { data, loading, error } = useAsync<GovernanceData>(() => aaiApi.getGovernance(), 'Could not load governance.');
  // Secondary, non-blocking reads that enrich the policy cards. If either is still
  // loading, fails, or is empty, the affected card degrades to an honest not-set state.
  const policy = useAsync<AaiPolicy>(() => aaiApi.getPolicy(), 'Could not load policy.');
  const allowed = useAsync<WhatsAllowedData>(() => aaiApi.getWhatsAllowed(), "Could not load what's allowed.");

  if (loading) return <Loading label="Loading governance…" />;
  if (error || !data) return <ErrorNote message={error || 'Something went wrong.'} />;

  // Card 1 — acceptable-use policy (org policy doc, Module 1 acceptable-use step).
  const pol = policy.data;
  const hasPolicy = !!(pol && pol.body && pol.body.trim());
  const policyDate = fmtDate(pol?.updated_at);

  // Card 2 — approved-tool list (formally cleared tools = published vendor evaluations).
  const cleared = distinct((allowed.data?.approved_tools ?? []).flatMap((t) => t.tools));
  const inUseCount = allowed.data?.tools_in_use?.length ?? 0;

  // Card 4 — human-in-the-loop coverage over High-risk registry rows.
  const highRisk = data.registry.filter((r) => (r.risk || '').toLowerCase() === 'high');
  const highWithHil = highRisk.filter((r) => r.human_in_loop && r.human_in_loop.trim() && !/unset/i.test(r.human_in_loop)).length;

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="Risk & governance"
        subtitle={`The guardrails behind the program — your AI use-case inventory by risk, mapped to ${data.framework}, plus the privacy rules and audit trail.`}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard value={data.use_case_count} label="Use cases" />
        <StatCard value={data.by_risk.low || 0} label="Low risk" valueClass="text-emerald-600" />
        <StatCard value={data.by_risk.medium || 0} label="Medium risk" valueClass="text-amber-600" />
        <StatCard value={data.by_risk.high || 0} label="High risk" valueClass="text-red-600" />
      </div>

      {/* Policy cards — the four standing policies, real data where the page has it. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <PolicyCard
          icon={FileText}
          title="Acceptable-use policy"
          status={hasPolicy ? 'Active' : 'Not set'}
          muted={!hasPolicy}
          note={
            policy.loading
              ? 'Checking policy status…'
              : hasPolicy
                ? `Published${policyDate ? ` · updated ${policyDate}` : ''}${pol?.updated_by ? ` · by ${pol.updated_by}` : ''}`
                : 'Not published yet — set it in Module 1 (acceptable use)'
          }
        />
        <PolicyCard
          icon={ListChecks}
          title="Approved-tool list"
          status={cleared.length ? 'Active' : 'Not set'}
          muted={cleared.length === 0}
          note={
            allowed.loading
              ? 'Checking cleared tools…'
              : cleared.length
                ? `${cleared.length} cleared: ${cleared.slice(0, 3).join(', ')}${cleared.length > 3 ? ` +${cleared.length - 3} more` : ''}`
                : inUseCount
                  ? `None formally cleared yet · ${inUseCount} tool${inUseCount === 1 ? '' : 's'} detected in use`
                  : 'No tools cleared yet — see What’s allowed'
          }
        />
        <PolicyCard
          icon={Database}
          title="Data-usage rules"
          status="Enforced"
          note="Group-size suppression, self-report discount and audited access enforced platform-wide — see Privacy & audit controls below."
        />
        <PolicyCard
          icon={UserCheck}
          title="Human-in-the-loop requirements"
          status={highRisk.length ? 'Enforced' : 'None yet'}
          muted={highRisk.length === 0}
          note={
            highRisk.length
              ? `${highWithHil} of ${highRisk.length} High-risk use case${highRisk.length === 1 ? '' : 's'} record a human-in-loop step`
              : 'No High-risk use cases in the registry yet'
          }
        />
      </div>

      {/* Responsible-AI controls — examiner-ready Control / Mapped rule / Status table. */}
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden mb-4">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
          <span className="font-bold text-sm">Responsible-AI controls</span>
          <span className="font-sans text-[10.5px] font-semibold text-purple-700 bg-purple-50 px-2.5 py-0.5 rounded-full">Examiner-ready</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead><tr className="text-left bg-gray-50/70 text-gray-400">
              {['Control', 'Mapped rule', 'Status'].map((h) => <th key={h} className="px-5 py-2.5 font-sans text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {data.controls.map((c) => (
                <tr key={c.name} className="border-t border-gray-50">
                  <td className="px-5 py-3 font-semibold">{c.name}</td>
                  <td className="px-5 py-3 font-sans text-[11.5px] text-gray-600">{c.detail || '—'}</td>
                  <td className={`px-5 py-3 font-semibold ${controlTone(c.status)}`}>{c.status || '—'}</td>
                </tr>
              ))}
              {data.controls.length === 0 && <tr><td colSpan={3} className="px-5 py-6 text-center text-[13px] text-gray-400">No responsible-AI controls recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Card className="mb-4">
        <MonoLabel className="mb-3">Privacy & audit controls</MonoLabel>
        <div className="space-y-2">
          {PRIVACY_CONTROLS.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.name} className="flex items-start gap-2.5 bg-white rounded-lg px-3.5 py-2.5">
                <Icon size={15} className="text-brand-500 mt-0.5 shrink-0" />
                <div><div className="text-[13px] font-semibold">{c.name}</div><div className="text-[12px] text-gray-500">{c.detail}</div></div>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 font-semibold text-sm">Use-case inventory</div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead><tr className="text-left bg-gray-50/70 text-gray-400">
              {['Use case', 'Dept', 'Risk', 'Model', 'Human-in-loop'].map((h) => <th key={h} className="px-4 py-2.5 font-sans text-[10.5px] uppercase tracking-wider font-semibold whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {data.registry.map((g, i) => (
                <tr key={i} className="border-t border-gray-50">
                  <td className="px-4 py-2.5 font-semibold">{g.name}</td>
                  <td className="px-4 py-2.5 text-gray-600">{g.department || '—'}</td>
                  <td className="px-4 py-2.5"><RiskPill risk={g.risk} /></td>
                  <td className="px-4 py-2.5 font-sans text-[11.5px] text-gray-600">{g.model || '— unset'}</td>
                  <td className="px-4 py-2.5 text-gray-600">{g.human_in_loop || '— unset'}</td>
                </tr>
              ))}
              {data.registry.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-[13px] text-gray-400">No approved use cases yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Design closer — governance is embedded in the workflow (footer strip, line 809). */}
      <div className="flex items-center gap-3 bg-brand-50 rounded-xl px-4 py-3.5 mt-4">
        <ShieldCheck size={18} className="text-brand-500 shrink-0" />
        <span className="text-[12.5px] text-gray-700">
          Governance is embedded in the workflow, not bolted on — a new use case can’t proceed without landing in the registry at its risk class.
        </span>
      </div>
    </div>
  );
};

export default RiskGovernancePage;
